import DOMPurify from "dompurify"

/**
 * Browser-side HTML purifier for AI-generated page/email HTML.
 *
 * IMPORTANT: this is the CLIENT-safe sanitizer. It imports `dompurify`
 * directly — in a browser DOMPurify binds to the real `window`, so there
 * is NO `jsdom` dependency. Do NOT use `lib/surveys/email-sanitizer.ts`
 * from client components: that one wraps DOMPurify with `jsdom` for
 * server use, and bundling `jsdom` (fs / child_process / net / tls) into
 * a client component breaks the Turbopack build.
 *
 * Strips the dangerous bits (`<script>`, `on*` handlers, `javascript:`
 * URLs) while PRESERVING document structure and `<style>` — AI-authored
 * phishing/landing/email pages legitimately need full documents and
 * inline CSS. `WHOLE_DOCUMENT: true` keeps `<html>/<head>/<body>`.
 *
 * Defense-in-depth only: the backend already sanitizes the AI output
 * server-side, and the review preview renders inside a script-less
 * sandboxed iframe. This pass guards what is written back to the editor.
 */
export function sanitizeHtmlClient(html: string): string {
  // SSR has no `window`; the AI result only ever lands client-side, so a
  // server-render pass-through is safe — the browser re-renders + this
  // function runs for real there.
  if (typeof window === "undefined") return html
  return DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    ADD_TAGS: ["style"],
    // DOMPurify already drops <script>, on* handlers and javascript: URLs
    // by default; keep style/structure intact for page authoring.
  })
}

/**
 * Fragment variant of {@link sanitizeHtmlClient} for HTML rendered INSIDE an
 * existing element (e.g. `<div dangerouslySetInnerHTML>`), NOT as a whole
 * document. Uses `WHOLE_DOCUMENT: false` (the default) so DOMPurify does not
 * re-wrap the output in `<html>/<head>/<body>` — which would corrupt a
 * fragment dropped into a `<div>`.
 *
 * Use this for UNTRUSTED, externally-sourced HTML such as an inbound phishing
 * reply body. (The backend also sanitizes that body at ingest; this is the
 * client-side defense-in-depth layer.)
 */
export function sanitizeHtmlFragment(html: string): string {
  if (typeof window === "undefined") return html
  return DOMPurify.sanitize(html, {
    ADD_TAGS: ["style"],
    // Default WHOLE_DOCUMENT: false — output is a fragment, not a document.
  })
}
